import React from 'react';
import { Rect, Label, Tag, Text, Group } from 'react-konva';
import { useUserSelections } from '../../hooks/useOptimisticPreview';
import * as awarenessProtocol from 'y-protocols/awareness';

interface SelectionOverlayProps {
  shapeId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  awareness: awarenessProtocol.Awareness | null;
  currentUserId?: string;
}

// Build an O(1) index of shapeId -> selector infos, shared across overlays
function useSelectionIndex(awareness: awarenessProtocol.Awareness | null) {
  const selections = useUserSelections(awareness);
  return React.useMemo(() => {
    const index = new Map<string, Array<{ clientId: string; user: { id: string; name: string; color: string } }>>();
    selections.forEach((data, clientId) => {
      data.selection.forEach((shapeId) => {
        const arr = index.get(shapeId);
        const entry = { clientId, user: data.user };
        if (arr) arr.push(entry);
        else index.set(shapeId, [entry]);
      });
    });
    return index;
  }, [selections]);
}

export const SelectionOverlay: React.FC<SelectionOverlayProps> = ({
  shapeId,
  x,
  y,
  width,
  height,
  awareness,
  currentUserId,
}) => {
  const index = useSelectionIndex(awareness);
  const selectors = React.useMemo(() => {
    if (!awareness) return [] as Array<{ user: { name: string; color: string } }>;
    const all = index.get(shapeId) || [];
    const currentClientId = String(awareness.clientID);
    const normalizedCurrentUserId = currentUserId?.trim().toLowerCase();
    return all
      .filter((d) => {
        if (d.clientId === currentClientId) return false;
        if (normalizedCurrentUserId && d.user.id?.trim().toLowerCase() === normalizedCurrentUserId) return false;
        return true;
      })
      .map((d) => ({ user: d.user }));
  }, [index, shapeId, awareness, currentUserId]);
  
  if (selectors.length === 0) return null;
  
  // Use the first selector's color (could blend multiple in the future)
  const selector = selectors[0];
  const color = selector.user.color;
  
  // Nodes use Konva's default: x,y at top-left of the node's local box
  const left = x;
  const top = y;
  
  return (
    <Group listening={false}>
      {/* Selection outline */}
      <Rect
        x={left - 3}
        y={top - 3}
        width={width + 6}
        height={height + 6}
        stroke={color}
        strokeWidth={2}
        dash={[5, 5]}
        fill="transparent"
      />
      
      {/* User label */}
      <Label
        x={left}
        y={top - 28}
        opacity={0.9}
      >
        <Tag
          fill={color}
          cornerRadius={3}
          pointerDirection="down"
          pointerWidth={6}
          pointerHeight={4}
        />
        <Text
          text={` ${selector.user.name} `}
          fontSize={11}
          fontFamily="system-ui"
          fill="white"
          padding={3}
        />
      </Label>
    </Group>
  );
};

interface MultiSelectionOverlayProps {
  selectedIds: Set<string>;
  shapes: Map<string, { x: number; y: number; width: number; height: number }>;
  userColor: string;
  showLabel?: boolean;
  userName?: string;
}

export const MultiSelectionOverlay: React.FC<MultiSelectionOverlayProps> = ({
  selectedIds,
  shapes,
  userColor,
  showLabel = false,
  userName
}) => {
  if (selectedIds.size === 0) return null;
  
  // Calculate bounding box of all selected shapes
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;
  
  selectedIds.forEach(id => {
    const shape = shapes.get(id);
    if (shape) {
      const left = shape.x;
      const top = shape.y;
      const right = shape.x + shape.width;
      const bottom = shape.y + shape.height;
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    }
  });
  
  if (!isFinite(minX)) return null;
  
  const boundingWidth = maxX - minX;
  const boundingHeight = maxY - minY;
  
  return (
    <Group listening={false}>
      {/* Selection rectangle for multiple items */}
      <Rect
        x={minX - 4}
        y={minY - 4}
        width={boundingWidth + 8}
        height={boundingHeight + 8}
        stroke={userColor}
        strokeWidth={2}
        dash={[8, 4]}
        fill="transparent"
        opacity={0.8}
      />
      
      {/* Optional user label for multi-selection */}
      {showLabel && userName && (
        <Label
          x={minX}
          y={minY - 30}
          opacity={0.9}
        >
          <Tag
            fill={userColor}
            cornerRadius={3}
            pointerDirection="down"
            pointerWidth={6}
            pointerHeight={4}
          />
          <Text
            text={` ${userName} (${selectedIds.size} items) `}
            fontSize={11}
            fontFamily="system-ui"
            fill="white"
            padding={3}
          />
        </Label>
      )}
    </Group>
  );
};
